<script setup lang="ts">
import type { Hub } from '@/data/Hubs/types'
import { useDeleteHubMutation } from '@/data/Hubs/useDeleteHubMutation'
import { useResolveProjectRoute } from '@/modules/Project/useResolveProjectRoute'
import EditHubDialog from '@/modules/Workspaces/KnowledgeHub/Hubs/EditHubDialog.vue'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import IconButton from '@/uiKit/IconButton.vue'
import Menu from '@/uiKit/Menu'
import { useMagicKeys } from '@vueuse/core'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps<{ hub: Hub }>()

const { workspaceId } = useWorkspaceContext()
const { mutate: remove } = useDeleteHubMutation({ workspaceId })
const showEditDialog = ref(false)

const { alt } = useMagicKeys()
const showDevLinks = computed<boolean>(() => {
  const isDev = import.meta.env.DEV
  const isStaging = import.meta.env.MODE === 'staging'
  return (isDev || isStaging) && alt?.value === true
})
const resolveProjectRoute = useResolveProjectRoute()
const hubProjectRoute = resolveProjectRoute({
  projectId: props.hub.id,
  workspaceId: props.hub.workspaceId,
})
const fileProjectRoute = resolveProjectRoute({
  projectId: props.hub.fileProjectId,
  workspaceId: props.hub.workspaceId,
})
</script>

<template>
  <Menu.Root
    v-slot="{ getTriggerProps }"
    :positioning="{ placement: 'bottom-end', offset: { mainAxis: 4 } }"
    close-on-select
  >
    <IconButton
      v-bind="{ ...getTriggerProps() }"
      icon="more-dots"
      size="xl"
      rounded
      variant="transparent"
      :aria-label="`${hub.name} additional actions`"
      @click.stop.prevent
    />

    <Menu.Content class="min-w-40">
      <Menu.Item
        icon="edit"
        label="Edit"
        @click.stop="showEditDialog = true"
      />

      <Menu.Divider />

      <!-- Delete -->
      <Menu.Item
        critical
        icon="trash"
        label="Delete"
        @click.stop="remove(hub.id)"
      />

      <template v-if="showDevLinks">
        <Menu.Divider />
        <RouterLink
          :to="hubProjectRoute"
          class="outline-hidden"
        >
          <Menu.Item
            icon="table"
            label="Open Hub Project"
            @click.stop
          />
        </RouterLink>
        <RouterLink
          :to="fileProjectRoute"
          class="outline-hidden"
        >
          <Menu.Item
            icon="table"
            label="Open File Project"
            @click.stop
          />
        </RouterLink>
      </template>
    </Menu.Content>
  </Menu.Root>

  <EditHubDialog
    v-model="showEditDialog"
    :hub="hub"
  />
</template>
